import { useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';

interface AccountPageTileProps {
  accountName: string;
  lastUpdated?: string | null;
  accountNumberLast4: string;
  product: string;
}

function getLastUpdatedHours(lastUpdated: string | null = null): number {
  if (!lastUpdated) {
    return 0;
  }
  const now = new Date();
  const diffInMilliseconds = now.getTime() - new Date(lastUpdated).getTime();
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));

  return diffInHours;
}

const ExternalAccountPageTiles = ({
  accountName,
  lastUpdated,
  accountNumberLast4,
  product,
}: AccountPageTileProps) => {
  const router = useRouter();
  const formattedPrimaryCopy = `${accountName} ${toTitleCase(product)} • ${accountNumberLast4}`;

  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'externalAccountTiles',
  });
  const lastUpdatedHours = getLastUpdatedHours(lastUpdated);

  const moveMoneyPressExternalAccountEvent = usePressableAnalytics({
    analyticsId: 'moveMoneyExternalAccount',
  });

  const handlePress = () => {
    router.push('/move-money/transfer/transfer-money');
    moveMoneyPressExternalAccountEvent();
  };

  return (
    <View>
      <ListItem
        primaryCopy={translatedText('labels.accountName')}
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: formattedPrimaryCopy,
          },
        }}
        showsChevron={false}
      />

      {lastUpdatedHours ? (
        <>
          <View style={styles.dividerWrapper}>
            <HairLineDivider />
          </View>
          <ListItem
            primaryCopy={translatedText('labels.lastUpdated')}
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: `${lastUpdatedHours.toString()} ${translatedText('hoursAgo')}`,
              },
            }}
            showsChevron={false}
          />
        </>
      ) : null}

      <View style={styles.buttonWrapper}>
        <Button label={translatedText('moveMoney')} onPress={handlePress} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default ExternalAccountPageTiles;
